import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon, { type IconName } from './AppIcon';
import Button from './ui/Button';

/**
 * Full-page state shown when a route guard refuses the current user, so a
 * refusal reads as a permissions explanation instead of a silent redirect.
 */

const ROLE_LABEL_KEYS: Record<string, string> = {
  Hr: 'accessDenied.roles.Hr',
  Admin: 'accessDenied.roles.Admin',
  Manager: 'accessDenied.roles.Manager',
  Chief: 'accessDenied.roles.Chief',
  Employee: 'accessDenied.roles.Employee',
  AssetManagement: 'accessDenied.roles.AssetManagement',
  Operator: 'accessDenied.roles.Operator',
  Clocking: 'accessDenied.roles.Clocking',
};

export interface AccessDeniedProps {
  /**
   * Roles that WOULD have been allowed. Optional: a refusal that is not about the
   * viewer's role (a cross-tenant record, a deleted employee) has no role list to
   * name, and inventing one would be a lie.
   */
  allowedRoles?: string[];
  /** Overrides the default role-oriented heading. Already translated. */
  title?: string;
  /** Overrides the default role-oriented explanation. Already translated. */
  body?: string;
  /** Lucide icon name; defaults to the shield used for role refusals. */
  iconName?: IconName;
  /** Set false for refusals where "ask HR" is not the remedy (e.g. a wrong link). */
  showContactHint?: boolean;
}

export const AccessDenied: React.FC<AccessDeniedProps> = ({
  allowedRoles = [],
  title,
  body,
  iconName = 'ShieldAlert',
  showContactHint = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Roles without a label are dropped so the sentence never leaks a raw role key.
  const roleList = useMemo(() => {
    const labels = allowedRoles
      .map(role => ROLE_LABEL_KEYS[role])
      .filter((key): key is string => Boolean(key))
      .map(key => t(key));

    const unique = Array.from(new Set(labels));
    if (unique.length === 0) return '';
    if (unique.length === 1) return unique[0];

    const head = unique.slice(0, -1).join(t('accessDenied.listSeparator'));
    return `${head}${t('accessDenied.listLastSeparator')}${unique[unique.length - 1]}`;
  }, [allowedRoles, t]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <section
        data-testid="access-denied"
        aria-labelledby="access-denied-heading"
        className="w-full max-w-lg rounded-2xl border border-border bg-card p-8 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:p-10"
      >
        <span className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
          <Icon name={iconName} size={26} className="text-muted-foreground" aria-hidden="true" />
        </span>

        <h1
          id="access-denied-heading"
          className="text-xl font-semibold tracking-tight text-foreground md:text-2xl"
        >
          {title ?? t('accessDenied.title')}
        </h1>

        <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
          {body ?? (roleList ? t('accessDenied.body', { roles: roleList }) : t('accessDenied.bodyGeneric'))}
        </p>

        {showContactHint && (
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {t('accessDenied.contactHint')}
          </p>
        )}

        <Button
          className="mt-7"
          iconName="LayoutDashboard"
          iconPosition="left"
          onClick={() => navigate('/')}
        >
          {t('accessDenied.backToDashboard')}
        </Button>
      </section>
    </div>
  );
};

export default AccessDenied;
